import { compose } from 'redux';
import { memo, useMemo } from 'react';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ExpandLess from '@material-ui/icons/ExpandLess';
import withStyles from '@material-ui/styles/withStyles';
import { Link } from 'react-router-dom';
import { IconButton, Typography } from '@material-ui/core';

import { Table, TableRowActionsCell, TableRow } from 'altus-ui-components';

import { EMPTY_LIST, Format } from 'app/app.constants';
import { toField, toWellbore } from 'utils/route.util';
import { formatDate } from 'utils/format.util';
import moment from 'moment';

const AperioWellboresTable = ({
  classes,
  wellboresByField = EMPTY_LIST,
  onMouseEnter,
  onMouseLeave,
  ...rest
}) => {
  const columns = useMemo(
    () => [
      {
        id: 'placeholder',
        Header: <TableRowActionsCell minItems={1} />,
        Cell: <TableRowActionsCell minItems={1} />,
      },
      {
        xs: 3,
        id: 'name',
        Header: 'Name',
        accessor: (wellOrField) => wellOrField.get('name'),
        Cell: ({ row, value }) => {
          const isField = row.original.get('isField');

          if (isField) {
            return (
              <Link
                to={toField(
                  row.original?.get('fieldId'),
                  row.original?.get('id'),
                )}
                className={classes.wellHeader}
              >
                <Typography variant="h6">{value}</Typography>
              </Link>
            );
          }

          return value;
        },
      },
      {
        xs: 2,
        Header: 'Last Project Date',
        id: 'lastProject',
        accessor: (wellOrField) => wellOrField.get('lastProject'),
        Cell: ({ value }) => {
          if (!value) {
            return null;
          }

          return (
            <Typography noWrap variant="body2">
              <Typography variant="inherit" component="span">
                {formatDate(moment(value), Format.date_dayfirst)}
              </Typography>
            </Typography>
          );
        },
      },
      {
        xs: 3,
        Header: 'Main Obj. (Last Project)',
        id: 'lastMainOjectiveProject',
        accessor: (wellOrField) => wellOrField.get('lastMainOjectiveProject'),
        Cell: ({ value }) => {
          if (!value) {
            return null;
          }

          return (
            <Typography noWrap variant="body2">
              <Typography variant="inherit" component="span">
                {value}
              </Typography>
            </Typography>
          );
        },
      },
      {
        xs: 2,
        Header: 'Risk Score',
        id: 'riskScore',
        accessor: (wellOrField) => wellOrField.get('wellRiskScore'),
        Cell: ({ value }) => {
          if (!value) {
            return null;
          }

          return (
            <Typography noWrap variant="body2">
              <Typography variant="inherit" component="span">
                {value?.score?.toFixed(3)}
              </Typography>
            </Typography>
          );
        },
      },
      {
        xs: 2,
        Header: 'Suggested Integrity Re-log',
        id: 'reLogDate',
        accessor: (wellOrField) => wellOrField.get('wellRiskScore'),
        Cell: ({ value }) => {
          if (!value) {
            return null;
          }

          return (
            <Typography noWrap variant="body2">
              <Typography variant="inherit" component="span">
                {getReLogDate()}
              </Typography>
            </Typography>
          );
        },
      },
      {
        id: 'expander',
        Cell: ({ row }) => {
          if (!row.canExpand) return null;

          return (
            <IconButton
              {...row.getToggleRowExpandedProps()}
              title="Toggle wells"
            >
              {row.isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          );
        },
      },
    ],
    [classes],
  );

  const WellboreTableRow = ({ item, ...rest }) => {
    const isWell = item.get('isWell');
    const isField = item.get('isField');

    if (isField) {
      return (
        <TableRow
          {...rest}
          item={item}
          classes={{
            root: classes.fieldRoot,
          }}
        />
      );
    }

    if (isWell) {
      return (
        <TableRow
          {...rest}
          item={item}
          classes={{
            root: classes.wellRoot,
          }}
        />
      );
    }

    return (
      <Link to={toWellbore(item.get('fieldId'), item.get('id'))}>
        <TableRow
          {...rest}
          item={item}
          title="Open well"
          onMouseEnter={() => onMouseEnter(item.toJS())}
          onMouseLeave={onMouseLeave}
        />
      </Link>
    );
  };

  return (
    <Table
      useExpanded
      usePagination
      subrowOffset={3}
      manualPagination
      columns={columns}
      useGlobalFilter={false}
      items={wellboresByField}
      paginateExpandedRows={false}
      TableRowComponent={WellboreTableRow}
      noItemsMessage="No matching wells..."
      {...rest}
    />
  );
};

const styles = (theme) => ({
  surveyUploadedDescription: {
    color: theme.palette.secondary.mainLight,
  },
  wellHeader: {
    color: theme.palette.common.white,
  },
  wellRoot: {
    '&:hover': {
      background: theme.altus.components.TableRow.background,
    },
  },
  fieldRoot: {
    top: 0,
    position: 'sticky',
    background: theme.altus.components.TableRow.hoverColor,
    '&:hover': {
      background: theme.altus.components.TableRow.hoverColor,
    },
  },
});

export default compose(withStyles(styles), memo)(AperioWellboresTable);

//temp function for re-log date
const getReLogDate = () => {
  const date = new Date();
  return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear() + 1}`;
};
